export type Verdict = "match" | "twijfel" | "nieuw";

export interface MatchComponents {
  total: number;
  phash: number;
  structuur: number;
  anchors: number;
}

export interface MatchHit {
  type_id: number;
  naam: string;
  afzender: string | null;
  score: number;
  components: MatchComponents;
}

export interface Fingerprint {
  versie: number;
  phash: string;
  structuur_hash: string;
  anchors: string[];
  paginas: number;
  aspect_ratio: number | null;
}

export interface LayoutSummary {
  paginas: number;
  per_pagina: Array<{
    page_index: number;
    results: number;
    layout_regions: number;
    tables: number;
    text_layer_quality: string;
    mode: string;
  }>;
}

export interface SuggestedField {
  veld: string;
  strategie: string;
  label: string;
  patroon: string;
  voorbeeld_bbox?: number[][] | null;
}

export interface SuggestedTable {
  tabel_naam: string;
  strategie: string;
  kolom_labels: string[];
  kolommen: string[];
  confidence?: number | null;
}

export interface SuggestedTemplate {
  versie: number;
  velden: SuggestedField[];
  tabellen: SuggestedTable[];
  ongebruikt: string[];
}

export type Bbox = Array<[number, number]>; // 4 punten: TL, TR, BR, BL in page-px

export interface VeldHit {
  page: number;
  bbox: Bbox | null;
  source: "result" | "table";
}

export interface TabelMeta {
  page: number;
  bbox: Bbox | null;
}

export interface MistHint {
  page: number;
  bbox: Bbox | null;
  label: string;
}

export interface MistCandidate {
  value: string;
  page: number | null;
  bbox: Bbox | null;
  source: "result" | "table" | null;
  context: string;
}

export interface ValidatieResultaat {
  formule: string;
  omschrijving?: string | null;
  status: "ok" | "afwijking" | "niet-berekenbaar";
  tolerantie?: number | null;
  links?: number | null;
  rechts?: number | null;
  verschil?: number | null;
  reden?: string | null;
}

export interface PageDim {
  page: number;
  width: number;
  height: number;
}

export interface ExtractedData {
  velden: Record<string, string>;
  velden_hits: Record<string, VeldHit>;
  tabellen: Record<string, Array<Record<string, string>>>;
  tabellen_meta: Record<string, TabelMeta>;
  mist: string[];
  mist_hints: Record<string, MistHint>;
  mist_candidates: Record<string, MistCandidate[]>;
  validaties: ValidatieResultaat[];
  page_dims: PageDim[];
}

export function pdfUrl(sha256: string): string {
  return `/api/upload/pdf/${sha256}`;
}

export interface UploadResponse {
  sha256: string;
  bytes: number;
  dropbox_pad_inbox: string;
  dropbox_pad_runs: string;
  fingerprint: Fingerprint;
  turboocr: LayoutSummary;
  turboocr_latency_ms: number;
  verdict: Verdict;
  matches: MatchHit[];
  suggested_template: SuggestedTemplate | null;
  extracted: ExtractedData | null;
  matched_type_id: number | null;
  matched_type_naam: string | null;
  extraction_run_id: number | null;
}

export interface DocumentType {
  id: number;
  naam: string;
  afzender: string | null;
  taal: string | null;
  categorie: string | null;
  fingerprint: Fingerprint;
  template: Record<string, unknown>;
  gebruik_count: number;
}

export async function uploadPdf(file: File, forceOcr: boolean = false): Promise<UploadResponse> {
  const fd = new FormData();
  fd.append("file", file);
  const url = forceOcr ? "/api/upload?force_ocr=true" : "/api/upload";
  const r = await fetch(url, { method: "POST", body: fd });
  if (!r.ok) throw new Error(`Upload faalde: ${r.status} ${await r.text()}`);
  return r.json();
}

export async function listTypes(): Promise<DocumentType[]> {
  const r = await fetch("/api/types");
  if (!r.ok) throw new Error(`listTypes: ${r.status}`);
  return r.json();
}

export async function createType(payload: {
  naam: string;
  afzender: string | null;
  taal: string | null;
  categorie: string | null;
  fingerprint: Fingerprint;
  template?: Record<string, unknown>;
}): Promise<DocumentType> {
  const r = await fetch("/api/types", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ template: {}, ...payload }),
  });
  if (!r.ok) throw new Error(`createType: ${r.status} ${await r.text()}`);
  return r.json();
}

export async function deleteType(id: number): Promise<void> {
  const r = await fetch(`/api/types/${id}`, { method: "DELETE" });
  if (!r.ok) throw new Error(`deleteType: ${r.status}`);
}

export async function updateType(
  id: number,
  patch: Partial<Pick<DocumentType, "naam" | "afzender" | "taal" | "categorie">>
): Promise<DocumentType> {
  const r = await fetch(`/api/types/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!r.ok) throw new Error(`updateType: ${r.status} ${await r.text()}`);
  return r.json();
}

export async function addFingerprint(
  typeId: number,
  fingerprint: Fingerprint
): Promise<DocumentType> {
  const r = await fetch(`/api/types/${typeId}/add-fingerprint`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ fingerprint }),
  });
  if (!r.ok) throw new Error(`addFingerprint: ${r.status} ${await r.text()}`);
  return r.json();
}

export async function me(): Promise<{ user: { email: string; name: string } | null }> {
  const r = await fetch("/auth/me");
  return r.json();
}

// ---- Learned aliases (runtime-geleerde label-varianten per doctype) --------

export interface LearnedAlias {
  id: number;
  doctype: string;
  veld_canoniek: string;
  taal: string;
  label: string;
  added_at: string;
  source_sha256: string | null;
  disabled: boolean;
  disabled_reden: string | null;
  bijgewerkt_at: string | null;
}

export async function listLearnedAliases(
  doctype?: string,
  includeDisabled: boolean = false
): Promise<LearnedAlias[]> {
  const params = new URLSearchParams();
  if (doctype) params.set("doctype", doctype);
  if (includeDisabled) params.set("include_disabled", "true");
  const q = params.toString();
  const r = await fetch(`/api/learned-aliases${q ? "?" + q : ""}`);
  if (!r.ok) throw new Error(`listLearnedAliases: ${r.status}`);
  return r.json();
}

export async function learnedAliasStats(): Promise<Record<string, number>> {
  const r = await fetch("/api/learned-aliases/stats");
  if (!r.ok) throw new Error(`learnedAliasStats: ${r.status}`);
  return r.json();
}

export async function createLearnedAlias(payload: {
  doctype: string;
  veld_canoniek: string;
  taal: string;
  label: string;
}): Promise<LearnedAlias> {
  const r = await fetch("/api/learned-aliases", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!r.ok) throw new Error(`createLearnedAlias: ${r.status} ${await r.text()}`);
  return r.json();
}

export async function updateLearnedAliasLabel(id: number, label: string): Promise<void> {
  const r = await fetch(`/api/learned-aliases/${id}/label`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ label }),
  });
  if (!r.ok) throw new Error(`updateLearnedAliasLabel: ${r.status}`);
}

export async function setLearnedAliasDisabled(
  id: number,
  disabled: boolean,
  reden?: string
): Promise<void> {
  const r = await fetch(`/api/learned-aliases/${id}/disabled`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ disabled, reden: reden ?? null }),
  });
  if (!r.ok) throw new Error(`setLearnedAliasDisabled: ${r.status}`);
}

export async function deleteLearnedAlias(id: number): Promise<void> {
  const r = await fetch(`/api/learned-aliases/${id}`, { method: "DELETE" });
  if (!r.ok) throw new Error(`deleteLearnedAlias: ${r.status}`);
}
